import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { universityBadge } from '../lib/universityBadge'
import { UniversityLogo } from '../components/UniversityLogo'
import { visibleFirstName } from '../lib/visibleFirstName'

/**
 * UniversitiesPage - onboarding-v2 entry point.
 *
 * First screen after login for a student without any committed programme.
 * Shows a grid of active universities; on click we:
 *   1. POST /auth/commit-university (persists target_university)
 *   2. Navigate to /courses/:shortName for programme pick.
 *
 * If exactly one university is active, auto-redirects (no point in a
 * single-card picker).
 */

interface UniversityRow {
  id:                         string
  name:                       string
  short_name:                 string
  max_options_per_candidate:  number
  /** Admin-uploaded logo override (POST /admin/universities/:id/logo).
   *  Null = the <UniversityLogo /> component falls back to the
   *  convention path /uni-logos/{slug}.{png|jpeg} or, last resort, a
   *  letter-circle. */
  logo_url:                   string | null
  /** Admission year this university's courses, vagas and weights reflect.
   *  Institutions publish editais on their own schedules, so this is shown
   *  per card rather than as one claim for the whole page. Null when the
   *  year was never recorded — the marker is then omitted rather than
   *  guessed. */
  edital_year:                number | null
  _count: { programmes: number }
}

export const UniversitiesPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, loading: authLoading, refresh } = useAuth()
  const [universities, setUniversities] = useState<UniversityRow[]>([])
  const [loading, setLoading]           = useState(true)
  const [committingTo, setCommittingTo] = useState<string | null>(null)
  const [error, setError]               = useState<string | null>(null)
  const isAddFlow = searchParams.get('add') === '1'

  const isAuthFailure = (err: any) =>
    err?.status === 401 || err?.data?.error === 'Missing or invalid authorization header'

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const d = await api.get<{ universities: UniversityRow[] }>('/universities')
        if (cancelled) return
        setUniversities(d.universities)
        if (d.universities.length === 1) {
          const only = d.universities[0]
          commit(only.short_name).catch(() => {/* surfaced inline */})
        }
      } catch (err: any) {
        if (isAuthFailure(err)) {
          try {
            await refresh()
            const retry = await api.get<{ universities: UniversityRow[] }>('/universities')
            if (cancelled) return
            setUniversities(retry.universities)
            if (retry.universities.length === 1) {
              const only = retry.universities[0]
              commit(only.short_name).catch(() => {/* surfaced inline */})
            }
            return
          } catch (retryErr: any) {
            if (isAuthFailure(retryErr)) {
              if (!cancelled) navigate('/login', { replace: true })
              return
            }
            if (!cancelled) {
              setError(retryErr?.data?.error ?? retryErr?.message ?? 'Erro ao carregar universidades')
            }
            return
          }
        }
        if (!cancelled) {
          setError(err?.data?.error ?? err?.message ?? 'Erro ao carregar universidades')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const commit = async (short_name: string) => {
    setCommittingTo(short_name)
    setError(null)
    try {
      await api.post('/auth/commit-university', { university_short_name: short_name })
      navigate(`/courses/${encodeURIComponent(short_name)}`)
    } catch (err: any) {
      if (isAuthFailure(err)) {
        try {
          await refresh()
          await api.post('/auth/commit-university', { university_short_name: short_name })
          navigate(`/courses/${encodeURIComponent(short_name)}`)
          return
        } catch (retryErr: any) {
          if (isAuthFailure(retryErr)) {
            navigate('/login', { replace: true })
            return
          }
          setError(retryErr?.data?.error ?? retryErr?.message ?? 'Erro ao guardar universidade')
        }
      } else {
        setError(err?.data?.error ?? err?.message ?? 'Erro ao guardar universidade')
      }
    } finally {
      setCommittingTo(null)
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">A carregar…</div>
      </div>
    )
  }

  if (user && !user.is_anonymous && user.has_programme && !isAddFlow) {
    return <Navigate to="/subjects" replace />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <img src="/brand/logo.svg" alt="Tutor" className="h-6" />
            <h1 className="sr-only">Tutor</h1>
          </div>
          {user && !user.is_anonymous ? (
            <span className="text-sm text-gray-500">{visibleFirstName(user?.name)}</span>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-blue-700 hover:text-blue-800"
            >
              Já tenho conta
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-1">Para que universidade te preparas?</h2>
          <p className="text-sm text-gray-500">Escolhe uma para começar. Podes mudar ou adicionar outras mais tarde.</p>

          {/* Editais disclaimer (Companion-identity PR follow-up,
              2026-05-04; rewritten 2026-10-14). This used to name a single
              year for the whole page, which held only while every
              institution moved together. They don't: UEM, ACIPOL and AM are
              on 2027 while the other nine are still on 2026, so one sentence
              can no longer be honest about all of them. The year now lives on
              each university card (`edital_year`) and this note points at it
              instead of repeating it. Voice: tu form, gender-neutral, honest. */}
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Nota: </span>
            os dados baseiam-se no edital indicado em cada universidade. Quando uma
            universidade publicar um novo edital, actualizamos e avisamos-te se houver
            mudanças no teu curso.
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {universities.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-12">Nenhuma universidade activa de momento.</p>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {universities.map(u => (
              <button
                key={u.id}
                onClick={() => commit(u.short_name)}
                disabled={committingTo !== null}
                className={`bg-white border border-gray-100 rounded-xl p-5 text-left hover:border-indigo-300 hover:shadow-sm transition-all disabled:opacity-50 ${
                  committingTo === u.short_name ? 'border-indigo-400 bg-indigo-50/30' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <UniversityLogo shortName={u.short_name} name={u.name} logoUrl={u.logo_url} size="md" />
                    <div className="bg-indigo-100 rounded-md px-2.5 py-1 flex items-center justify-center">
                      <span className="text-indigo-700 font-black text-sm whitespace-nowrap">{universityBadge(u.short_name)}</span>
                    </div>
                  </div>
                  {committingTo === u.short_name && <span className="text-xs text-indigo-600">A carregar…</span>}
                </div>
                <h3 className="font-semibold text-gray-900 text-sm leading-snug">{u.name}</h3>
                <p className="text-xs text-gray-400 mt-1">
                  {u._count.programmes} cursos disponíveis
                  {u.max_options_per_candidate >= 2 && ' · admite 2 opções'}
                </p>
                {/* Which edital these figures come from. Sits on the card, not
                    in the page note, because universities move one at a time. */}
                {u.edital_year !== null && (
                  <p className="text-[11px] text-gray-400 mt-1">Edital {u.edital_year}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
